import {
	type Capture,
	type CollectionFieldFileTool,
	type CollectionJSONObject,
	type CollectionSummaryJSONObject,
	ParseJSON,
	type CaptureFileJSONObject,
} from '@ikegps/core-api';
import type { Log } from 'bblog';
import { IkeId } from '../../id';
import type { FormModel } from '../form/form';
import { CollectionModel } from './collection.data';
import { type IkeCollectionLoader, IkeCollectionLoaderFromState } from './collection.loader';
import { CaptureProviderHelpers } from './fields/provider/capture.provider';
import { CollectionProviderHelpers } from './fields/provider/collection.provider';
import { IkeModelCollectionReader } from './serializer/collection.model.read';
import { findObjectsWhere, isCaptureData, isFileData, isLink } from './serializer/collection.model.util';
import { IkeModelCollectionWriter } from './serializer/collection.model.write';
import { IkeCollectionUpgrade } from './upgrade/collection.upgrade';

export class IkeModelCollection {
	/** v3 collections have had their captures extracted into the capture file table */
	static readonly JSON_VERSION_3_PARSE_API = 3;
	/** v4 Collections have re-mapped their captures to include the objectId and raw values of the actual capture. */
	static readonly JSON_VERSION_4_CAPTURE_FIXED = 4;
	/** v5 Collections  */
	static readonly JSON_VERSION_5_BRIGHTNESS_CORRECTION_ADDED = 5;
	/** v6 Collections  */
	static readonly JSON_VERSION_6_CROP_PARAMETER_ADDED = 6;

	static readonly JSON_VERSION_LATEST = 6;

	loader() {
		return new IkeCollectionLoaderFromState();
	}

	upgrade(collections: CollectionModel[], targetForm: FormModel) {
		return new IkeCollectionUpgrade(collections, targetForm);
	}

	create(form: FormModel, loader: IkeCollectionLoader): CollectionModel {
		const collection = new CollectionModel(form, loader);
		collection.state.uuid = IkeId.v4();
		collection.state.revision = collection.state.instanceId = IkeId.generate();
		collection.state.createdAt = collection.state.updatedAt = new Date();
		return collection;
	}

	read(collection: CollectionJSONObject, schema: FormModel, loader: IkeCollectionLoader, log?: Log): CollectionModel {
		const reader = new IkeModelCollectionReader(collection, schema, loader);
		if (log) {
			reader.log = log;
		}
		return reader.read();
	}

	async write(model: CollectionModel): Promise<CollectionJSONObject> {
		const collectedAt = await model.getCollectedAt();
		const collectionJSON: CollectionJSONObject = {
			objectId: model.id,
			v: IkeModelCollection.JSON_VERSION_LATEST,
			uuid: model.uuid,
			data: {
				recordeduuid: model.uuid,
				uuid: model.schema.rootFormId,
				version: model.schema.revision,
				data: IkeModelCollectionWriter.writeData(model),
			},
			formuuid: model.schema.rootFormId,
			formversion: model.schema.revision,
			jobuuid: model.jobId,
			measurements: IkeModelCollectionWriter.writeCollectionMeasurements(model),
			brightnessCorrections: IkeModelCollectionWriter.writeCollectionBrightnessCorrections(model),
			cropParameters: IkeModelCollectionWriter.writeCollectionCropParameters(model),
			imageVisibilities: IkeModelCollectionWriter.writeImageVisibilities(model),
			revision: model.revision,
			collectedBy: model.collectedBy,
			collectedWith: model.collectedWith,
			assignedTo: model.assignedTo,
			collectedAt: ParseJSON.toDate(collectedAt),
			createdAt: model.createdAt != null ? model.createdAt.toISOString() : null,
			updatedAt: model.updatedAt != null ? model.updatedAt.toISOString() : null,
			updatedBy: model.updatedBy,
		};

		// Variant fields are omitted (undefined) when absent — never serialized as null.
		if (model.variantType !== undefined) {
			collectionJSON.variantType = model.variantType;
		}
		if (model.variantTypeName !== undefined) {
			collectionJSON.variantTypeName = model.variantTypeName;
		}
		if (model.variantForUuid !== undefined) {
			collectionJSON.variantForUuid = model.variantForUuid;
		}
		if (model.variants !== undefined && model.variants.length > 0) {
			collectionJSON.variants = model.variants;
		}

		if (collectedAt !== model.createdAt && collectedAt != null) {
			collectionJSON.data.createdat = collectedAt.getTime() / 1000;
		}

		return collectionJSON;
	}

	/**
	 * Build a collection summary from a collection model.
	 *
	 * TODO: `link`, `updatedBy`, and `tags` are not fully implemented.
	 */
	summary(model: CollectionModel): CollectionSummaryJSONObject {
		const summaryObject: CollectionSummaryJSONObject = {
			objectId: model.id,
			collectionId: model.id,
			revision: model.revision,
			uuid: model.uuid,
			formRevision: model.schema.revision,
			createdAt: model.createdAt.toISOString(),
			updatedAt: model.updatedAt.toISOString(),
			collectedAt: ParseJSON.toDate(model.collectedAt),
			collectedBy: model.collectedBy,
			collectedWith: model.collectedWith,
			assignedTo: model.assignedTo,
			links: CollectionProviderHelpers.getCollectionIds(model),
			updatedBy: [model.updatedBy],
			title: model.title,
			subtitle: model.subtitle,
			siteType: model.siteType,
			dropPole: model.dropPole,
			poleLoading: model.poleLoading,
			mapMarkerColor: model.mapMarkerColor,
			status: null,
			location: ParseJSON.toGeoPoint(model.location),
			captureIds: CaptureProviderHelpers.getCaptureIds(model),
			tags: [model.jobId],
		};

		const statusField = model.status;
		if (statusField != null) {
			if (statusField.hasValue()) {
				summaryObject.status = statusField.displayValue;
			}
		}

		// Variant fields are omitted (undefined) when absent — never set to null.
		if (model.variantType !== undefined) {
			summaryObject.variantType = model.variantType;
		}
		if (model.variantTypeName !== undefined) {
			summaryObject.variantTypeName = model.variantTypeName;
		}
		if (model.variantForUuid !== undefined) {
			summaryObject.variantForUuid = model.variantForUuid;
		}
		if (model.variants !== undefined && model.variants.length > 0) {
			summaryObject.variants = model.variants;
		}

		return summaryObject;
	}

	getCaptureUUIDs(collection: CollectionJSONObject): string[] {
		const collectionCaptureData = findObjectsWhere(collection, isCaptureData) as Capture[];

		const uuidMap: { [uuid: string]: boolean } = {};
		for (const captureData of collectionCaptureData) {
			const uuid = captureData.camera.imageuuid;
			if (uuid != null && !uuidMap[uuid]) {
				uuidMap[uuid] = true;
			}
		}

		return Object.keys(uuidMap);
	}

	getLinkUUIDs(collection: CollectionJSONObject): string[] {
		const collectionLinkData = findObjectsWhere(collection, isLink);

		const uuidMap: { [uuid: string]: boolean } = {};
		for (const linkData of collectionLinkData) {
			const uuid = linkData.collectionUuid;
			if (uuid != null && !uuidMap[uuid]) {
				uuidMap[uuid] = true;
			}
		}

		return Object.keys(uuidMap);
	}

	getCalibrationIds(captures: CaptureFileJSONObject[]): string[] {
		const calibrationIdMap: { [id: string]: boolean } = {};
		for (const capture of captures) {
			const calibrationId = capture.calibrationid;
			if (calibrationId != null && !calibrationIdMap[calibrationId]) {
				calibrationIdMap[calibrationId] = true;
			}
		}

		return Object.keys(calibrationIdMap);
	}

	getFileIds(collection: CollectionJSONObject): string[] {
		const collectionFileData = findObjectsWhere(collection, isFileData) as CollectionFieldFileTool[];

		const idMap: { [id: string]: boolean } = {};
		for (const fileData of collectionFileData) {
			const id = fileData.attachmentId;
			if (id != null && !idMap[id]) {
				idMap[id] = true;
			}
		}

		return Object.keys(idMap);
	}
}
